import React from 'react';
import { Link } from 'react-router-dom';
import { Button } from './ui/button';
import { ArrowRight } from 'lucide-react';

/**
 * Reusable professional page banner
 * Props:
 *  - badge: small label above title
 *  - title: main heading (string or JSX)
 *  - subtitle: description text
 *  - ctaPrimary: { label, to } 
 *  - ctaSecondary: { label, to }
 *  - visual: React node for right column (custom per page)
 *  - dark: bool (default dark navy, false = gold)
 */
export const PageHero = ({
  badge,
  title,
  subtitle,
  ctaPrimary,
  ctaSecondary,
  visual,
  dark = true,
}) => {
  const bg = dark ? 'bg-[#0f172a]' : 'bg-[#D4A017]';
  const textColor = dark ? 'text-white' : 'text-[#0f172a]';
  const subColor = dark ? 'text-[#94a3b8]' : 'text-[#1e293b]';
  const badgeColor = dark ? 'text-[#D4A017]' : 'text-[#0f172a]/70';

  return (
    <section
      className={`${bg} pt-28 pb-16 md:pb-20 overflow-hidden relative`}
      style={{
        backgroundImage: dark
          ? `radial-gradient(circle at 20% 50%, rgba(212,160,23,0.07) 0%, transparent 60%), radial-gradient(circle at 80% 20%, rgba(212,160,23,0.05) 0%, transparent 50%)`
          : undefined,
      }}
    >
      {/* Decorative grid pattern */}
      <div
        className="absolute inset-0 opacity-[0.03]"
        style={{
          backgroundImage: `repeating-linear-gradient(0deg, transparent, transparent 39px, ${dark ? '#D4A017' : '#0f172a'} 39px, ${dark ? '#D4A017' : '#0f172a'} 40px), repeating-linear-gradient(90deg, transparent, transparent 39px, ${dark ? '#D4A017' : '#0f172a'} 39px, ${dark ? '#D4A017' : '#0f172a'} 40px)`,
        }}
      />

      {/* Gold accent bar */}
      <div className="absolute top-0 left-0 right-0 h-1 bg-gradient-to-r from-[#D4A017] via-[#f5c842] to-[#D4A017]" />

      <div className="container-custom relative z-10">
        <div className={`grid ${visual ? 'lg:grid-cols-2 gap-12' : 'max-w-3xl'} items-center`}>
          {/* Left: Text */}
          <div>
            {badge && (
              <span className={`inline-block text-xs font-bold uppercase tracking-widest ${badgeColor} mb-4`}>
                {badge}
              </span>
            )}
            <h1 className={`font-serif text-4xl md:text-5xl lg:text-6xl font-bold ${textColor} leading-tight mb-5`}>
              {title}
            </h1>
            {subtitle && (
              <p className={`text-lg ${subColor} leading-relaxed mb-8 max-w-xl`}>
                {subtitle}
              </p>
            )}
            {(ctaPrimary || ctaSecondary) && (
              <div className="flex flex-wrap gap-3">
                {ctaPrimary && (
                  <Link to={ctaPrimary.to}>
                    <Button className="bg-[#D4A017] hover:bg-[#B8860B] text-[#0f172a] font-bold px-7 py-5 text-base">
                      {ctaPrimary.label} <ArrowRight size={16} className="ml-2" />
                    </Button>
                  </Link>
                )}
                {ctaSecondary && (
                  <Link to={ctaSecondary.to}>
                    <Button
                      variant="outline"
                      className={`px-7 py-5 text-base font-bold ${
                        dark
                          ? 'border-white/30 text-white hover:bg-white/10'
                          : 'border-[#0f172a] text-[#0f172a] hover:bg-[#0f172a] hover:text-white'
                      }`}
                    >
                      {ctaSecondary.label}
                    </Button>
                  </Link>
                )}
              </div>
            )}
          </div>

          {/* Right: Visual */}
          {visual && (
            <div className="hidden lg:flex items-center justify-center">
              {visual}
            </div>
          )}
        </div>
      </div>
    </section>
  );
};
